import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';

export interface DockBadges {
  stock: number;
  team: number;
  docs: number;
  tasks: number;
  rh: number;
  redactia: number;
}

interface DockBadgesContextValue {
  badges: DockBadges;
  refresh: () => void;
}

const EMPTY: DockBadges = { stock: 0, team: 0, docs: 0, tasks: 0, rh: 0, redactia: 0 };
const POLL_INTERVAL = 30_000; // 30 seconds

const DockBadgesContext = createContext<DockBadgesContextValue>({ badges: EMPTY, refresh: () => {} });

export function DockBadgesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [badges, setBadges] = useState<DockBadges>(EMPTY);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const fetchBadges = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.getDockBadges();
      if (res.data) setBadges(res.data);
    } catch {
      // silent — non-blocking
    }
  }, [user]);

  useEffect(() => {
    fetchBadges();
    intervalRef.current = setInterval(fetchBadges, POLL_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchBadges]);

  return (
    <DockBadgesContext.Provider value={{ badges, refresh: fetchBadges }}>
      {children}
    </DockBadgesContext.Provider>
  );
}

export function useDockBadges() {
  return useContext(DockBadgesContext);
}

/** Returns true when the dock is pinned (always visible), false when auto-hidden */
export function useDockPinned() {
  return localStorage.getItem('dock-autohide') !== 'true';
}
